'use client';

import { useEffect, useRef, useCallback } from 'react';

const INACTIVITY_TIMEOUT_MS = 30_000; // 30 seconds
const THROTTLE_MS = 2_000; // 2 seconds

export function useActiveTime() {
  const activeTimeMsRef = useRef(0);
  const isActiveRef = useRef(false);
  const lastActiveStartRef = useRef(0);
  const lastEventTimeRef = useRef(0);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopAccumulating = useCallback(() => {
    if (isActiveRef.current) {
      activeTimeMsRef.current += Date.now() - lastActiveStartRef.current;
      isActiveRef.current = false;
    }
  }, []);

  const startAccumulating = useCallback(() => {
    if (!isActiveRef.current) {
      lastActiveStartRef.current = Date.now();
      isActiveRef.current = true;
    }
  }, []);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    inactivityTimerRef.current = setTimeout(() => {
      stopAccumulating();
    }, INACTIVITY_TIMEOUT_MS);
  }, [stopAccumulating]);

  useEffect(() => {
    const handleInteraction = () => {
      const now = Date.now();
      if (now - lastEventTimeRef.current < THROTTLE_MS) return;
      lastEventTimeRef.current = now;

      startAccumulating();
      resetInactivityTimer();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopAccumulating();
        if (inactivityTimerRef.current) {
          clearTimeout(inactivityTimerRef.current);
        }
      }
      // Resume only on next interaction, not on visibility alone
    };

    const events = ['mousemove', 'scroll', 'click', 'keydown', 'touchstart'] as const;
    events.forEach((event) => {
      window.addEventListener(event, handleInteraction, { passive: true });
    });
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Start tracking immediately (user just loaded the page)
    startAccumulating();
    resetInactivityTimer();

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleInteraction);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [startAccumulating, stopAccumulating, resetInactivityTimer]);

  const getActiveTimeMs = useCallback(() => {
    if (isActiveRef.current) {
      return activeTimeMsRef.current + (Date.now() - lastActiveStartRef.current);
    }
    return activeTimeMsRef.current;
  }, []);

  const reset = useCallback(() => {
    activeTimeMsRef.current = 0;
    isActiveRef.current = false;
    lastActiveStartRef.current = 0;
    lastEventTimeRef.current = 0;
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  return { getActiveTimeMs, reset };
}
